import { v4 as uuid4 } from 'uuid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { IActionRepeaterValue, IActionType } from '@makyoapp/makyo-common'
import { ColumnAdapter, TableAdapter } from '@makyoapp/makyo-common/dist/adapter/TableAdapter'
import { BuilderDroppableElement, ElementSetting } from '@makyoapp/makyo-common/dist/builder'

import { DateTimeDisplay } from '@/core/models/columns/dateTime'
import { DEFAULT_COMPONENTS_GROUPS } from '@/shared/builder/components'
import {
  BuilderComponent,
  BuilderComponentSetting,
  BuilderComponentSettingType,
  ComponentSettingKey,
} from '@/shared/builder/types'
import { getDefaultResponsiveSettings, hasResponsiveSettings } from '@/shared/builder/viewport/responsive-settings'
import { getDefaultMargin } from '@/shared/builder/viewport/utils'
import { FORM_VALIDATION_PATTERNS } from '@/shared/form-utils'

import { parseCompSettings, parseDefaultValue } from './comp-parsers'

export const FORM_FIELD_COLUMN_TYPES = [
  ColumnTypes.Text,
  ColumnTypes.RichText,
  ColumnTypes.Number,
  ColumnTypes.Email,
  ColumnTypes.Phone,
  ColumnTypes.URL,
  ColumnTypes.Color,
  ColumnTypes.Address,
  ColumnTypes.Coordinate,
  ColumnTypes.DateTime,
  ColumnTypes.Boolean,
  ColumnTypes.Image,
  ColumnTypes.ImageArray,
  ColumnTypes.Attachment,
  ColumnTypes.Rating,
  ColumnTypes.Icon,
]

export const ColumnTypeToFieldMap = new Map<string, Set<ColumnTypes>>([
  [
    'text-field',
    new Set([
      ColumnTypes.Text,
      ColumnTypes.RichText,
      ColumnTypes.Number,
      ColumnTypes.Address,
      ColumnTypes.URL,
      ColumnTypes.Email,
      ColumnTypes.Phone,
      ColumnTypes.Rating,
    ]),
  ],
  [
    'selection',
    new Set([
      ColumnTypes.Text,
      ColumnTypes.RichText,
      ColumnTypes.Number,
      ColumnTypes.Address,
      ColumnTypes.URL,
      ColumnTypes.Email,
      ColumnTypes.Phone,
      ColumnTypes.Rating,
    ]),
  ],
  ['checkbox', new Set([ColumnTypes.Boolean])],
  ['switch', new Set([ColumnTypes.Boolean])],
  ['slider', new Set([ColumnTypes.Number, ColumnTypes.Rating])],
  ['datetime-picker', new Set([ColumnTypes.DateTime])],
  ['switch', new Set([ColumnTypes.Boolean])],
  ['image-upload', new Set([ColumnTypes.Image, ColumnTypes.ImageArray])],
  ['file', new Set([ColumnTypes.Attachment])],
  ['field-rating', new Set([ColumnTypes.Rating])],
  ['file', new Set([ColumnTypes.Image, ColumnTypes.Attachment])],
  ['image-upload', new Set([ColumnTypes.Image, ColumnTypes.Attachment, ColumnTypes.ImageArray])],
  [
    'hidden-field',
    new Set([
      ColumnTypes.RowID,
      ColumnTypes.Text,
      ColumnTypes.Number,
      ColumnTypes.Email,
      ColumnTypes.Phone,
      ColumnTypes.URL,
      ColumnTypes.Color,
      ColumnTypes.Address,
      ColumnTypes.Coordinate,
      ColumnTypes.Image,
      ColumnTypes.DateTime,
      ColumnTypes.Boolean,
      ColumnTypes.Phone,
      ColumnTypes.Rating,
      ColumnTypes.Icon,
    ]),
  ],
])

function getFormFieldBasedOnColumnType(type: ColumnTypes) {
  switch (type) {
    case ColumnTypes.Text:
    case ColumnTypes.RichText:
    case ColumnTypes.Number:
    case ColumnTypes.Email:
    case ColumnTypes.URL:
    case ColumnTypes.Color:
    case ColumnTypes.Address:
    case ColumnTypes.Coordinate:
      return 'text-field'
    case ColumnTypes.Phone:
      return 'phone-input'
    case ColumnTypes.DateTime:
      return 'datetime-picker'
    case ColumnTypes.Boolean:
      return 'switch'
    case ColumnTypes.Image:
    case ColumnTypes.ImageArray:
      return 'image-upload'
    case ColumnTypes.Attachment:
      return 'file'
    case ColumnTypes.Rating:
      return 'field-rating'
    case ColumnTypes.Icon:
      return 'icon-picker'
    default:
      return null
  }
}

function getGroupId(compId: string) {
  return DEFAULT_COMPONENTS_GROUPS.find((g) => g.components.find((c) => c.compId === compId))?.id
}

const ALL_COMPONENTS = DEFAULT_COMPONENTS_GROUPS.flatMap((g) => g.components)
const FORM_COMP_GROUP = DEFAULT_COMPONENTS_GROUPS.find((g) => g.id === 'container')
const FORM_COMP = FORM_COMP_GROUP?.components.find((c) => c.compId === 'form')

const getFlattenedSettings = () => {
  if (!FORM_COMP) return []

  const comp = FORM_COMP

  const list = [...(comp.settings.data ?? []), ...(comp.settings.design ?? [])]

  if (comp.settings.dataGroup) {
    const stgs = Object.values(comp.settings.dataGroup)
    for (const s of stgs) {
      if (Array.isArray(s.items)) {
        list.push(...s.items)
      }
    }
  }
  if (comp.settings.designGroup) {
    const stgs = Object.values(comp.settings.designGroup)
    for (const s of stgs) {
      if (Array.isArray(s.items)) {
        list.push(...s.items)
      }
    }
  }
  if (comp.settings.behavior?.custom) {
    const stgs = Object.values(comp.settings.behavior?.custom)
    for (const s of stgs) {
      if (Array.isArray(s.items)) {
        list.push(...s.items)
      }
    }
  }

  return list
}

/**
 * return the pattern in FORM_VALIDATION_PATTERNS
 * @see {@link FORM_VALIDATION_PATTERNS}
 */
function getPattern(columnType: ColumnTypes) {
  switch (columnType) {
    // case ColumnTypes.Text:
    //   return 'none'
    // case ColumnTypes.Number:
    //   return 'positive_integer'
    // case ColumnTypes.Email:
    //   return 'email'
    // case ColumnTypes.Phone:
    //   return 'phone_basic'
    // case ColumnTypes.URL:
    //   return 'url'
    // case ColumnTypes.Coordinate:
    //   return 'coordinate'
    default:
      return 'none'
  }
}

export function parseFormSubmitButtonSettings(
  comp: BuilderComponent,
  tables: TableAdapter[],
  flattenedSettings: BuilderComponentSetting[],
  table: TableAdapter,
  type: 'form-update' | 'form-add' = 'form-add'
) {
  return {
    ...parseCompSettings(comp, {
      menus: [],
      tables,
      flattened: flattenedSettings,
    }),
    [ComponentSettingKey.ButtonDataLabel]: [
      {
        type: 'paragraph',
        children: [{ text: 'Submit' }],
      },
    ],
    [ComponentSettingKey.ButtonDesignMargin]: {
      phone: getDefaultMargin([0]),
      'phone-landscape': getDefaultMargin([0]),
      tablet: getDefaultMargin([0]),
      'tablet-landscape': getDefaultMargin([0]),
      desktop: getDefaultMargin([0]),
      tv: getDefaultMargin([0]),
    },
    [ComponentSettingKey.ButtonDesignWidth]: {
      phone: ['100%'],
      'phone-landscape': ['100%'],
      tablet: ['100%'],
      'tablet-landscape': ['100%'],
      desktop: ['100%'],
      tv: ['100%'],
    },
    [ComponentSettingKey.ButtonDesignFullWidth]: {
      phone: true,
      'phone-landscape': false,
      tablet: false,
      'tablet-landscape': false,
      desktop: false,
      tv: false,
      overflowSetting: {
        phone: true,
        'phone-landscape': true,
        tablet: true,
        'tablet-landscape': true,
        desktop: true,
        tv: true,
      },
    },
    [ComponentSettingKey.ButtonActionClick]: [
      {
        id: uuid4(),
        type: type === 'form-update' ? IActionType.FormUpdateRow : IActionType.FormAddRow,
        settings: {
          tableId: table.id,
          data: {},
        },
      },
      {
        id: uuid4(),
        type: IActionType.ShowSnackbar,
        settings: {
          bgColor: 'primary-20',
          value: [
            {
              type: 'paragraph',
              children: [
                { text: type === 'form-update' ? 'Item updated successfully' : 'New item added successfully' },
              ],
            },
          ],
        },
      },
    ] as IActionRepeaterValue[],
  }
}

function parseFieldItemSetting(
  tempSetting: ElementSetting,
  items: BuilderComponentSetting[],
  table: TableAdapter,
  column?: ColumnAdapter,
  isUserTable?: boolean
) {
  for (const item of items) {
    if (item.type === BuilderComponentSettingType.Group) {
      const items = item.groupItems ? Object.values(item.groupItems) : []
      if (items.length) {
        for (const groupItem of items) {
          parseFieldItemSetting(tempSetting, groupItem, table, column)
        }
      }
    }

    // parse default settings for every breakpoints
    let isDesign = hasResponsiveSettings(item.id)
    if (isDesign) {
      tempSetting[item.id] = getDefaultResponsiveSettings(item.id)
    } else if (item.defaultValue) {
      tempSetting[item.id] = parseDefaultValue(
        item.type,
        item.defaultValue,
        item.iconSelectSettings?.multiple ??
          item.selectSettings?.multiple ??
          item.variableSelectSettings?.multiple ??
          item.iconSelectSettings?.multiple ??
          item.screenSelectSettings?.multiple ??
          item.menuSelectSettings?.multiple,
        table.id
      )
    }

    // Form Field Settings
    // inject table data source and column from the parent form element
    if (table && column) {
      if (item.type === BuilderComponentSettingType.ColumnSelect) {
        tempSetting[item.id] = column.id
      }

      if (item.id.includes('-data-source')) {
        tempSetting[item.id] = table.id
      }

      if (item.id.includes('-data-label')) {
        if (item.type === BuilderComponentSettingType.VariableText) {
          tempSetting[item.id] = [
            {
              type: 'paragraph',
              children: [{ text: column?.name }],
            },
          ]
        } else if (item.type === BuilderComponentSettingType.Text) {
          tempSetting[item.id] = column?.name
        }
      }

      // // add leading icon for text column
      // if (item.id === ComponentSettingKey.TextFieldDataLeadingIcon && FieldTextColumnIconMap[column.type]) {
      //   tempSetting[item.id] = FieldTextColumnIconMap[column.type]
      // }

      // for richtext column, the TextFieldDesignHeight will be medium by default
      if (column.type === ColumnTypes.RichText && item.id === ComponentSettingKey.TextFieldDesignHeight) {
        tempSetting[item.id] = 'medium'
      }

      // for datetime column, the datetimepicker showTime will be based on column settings
      if (column.type === ColumnTypes.DateTime && item.id === ComponentSettingKey.DateTimePickerBehaviorShowTime) {
        const columnSettings = column.settings
        const dtDisplay = columnSettings.dtDisplay as DateTimeDisplay
        if (dtDisplay === 'datetime' || dtDisplay === 'time') {
          tempSetting[item.id] = true
        }
      }

      // add patteron for text field based on column type
      if (item.id === ComponentSettingKey.TextFieldBehaviorPattern) {
        const fieldPattern = getPattern(column.type)
        tempSetting[item.id] = FORM_VALIDATION_PATTERNS.find((p) => p.id === fieldPattern)?.pattern || 'none'
      }

      if (
        item.id === ComponentSettingKey.TextFieldBehaviorCharacterCounter &&
        [ColumnTypes.Text, ColumnTypes.RichText].includes(column.type)
      ) {
        tempSetting[item.id] = true
      }

      // disable email field for user table
      if (
        isUserTable &&
        column.type === ColumnTypes.Email &&
        item.id === ComponentSettingKey.TextFieldDataDefaultValueEditable
      ) {
        tempSetting[item.id] = false
      }
    }
  }
}

export function parseFormFieldSettings(
  comp: BuilderComponent,
  table: TableAdapter,
  column: ColumnAdapter,
  isUserTable?: boolean
) {
  const tempSetting: ElementSetting = {}

  if (comp.settings.dataGroup) {
    for (const group of Object.values(comp.settings.dataGroup)) {
      parseFieldItemSetting(tempSetting, group.items, table, column, isUserTable)
    }
  }

  if (comp.settings.data) {
    parseFieldItemSetting(tempSetting, comp.settings.data, table, column)
  }

  if (comp.settings.designGroup) {
    for (const group of Object.values(comp.settings.designGroup)) {
      parseFieldItemSetting(tempSetting, group.items, table, column)
    }
  }

  if (comp.settings.design) {
    parseFieldItemSetting(tempSetting, comp.settings.design, table, column)
  }

  if (comp.settings.behavior?.custom) {
    for (const group of Object.values(comp.settings.behavior.custom)) {
      parseFieldItemSetting(tempSetting, group.items, table, column)
    }
  }

  return tempSetting
}

/**
 * Generate Form Fields based on the table columns
 */
export function generateAutoFormFields(
  tables: TableAdapter[],
  formId: string,
  formSettings: Record<string, any>,
  detailTableId?: string,
  formType: 'form-update' | 'form-add' = 'form-add'
) {
  const dataSource = formSettings[ComponentSettingKey.FormDataSource]
  const table = tables.find((t) => t.id === dataSource)
  if (!table) return { children: [], childElSettings: {} }

  const isUserTable = table.isUserTable

  let elChildren: BuilderDroppableElement[] = []
  let elSettings: Record<string, any> = {}

  const flattenedSettings = getFlattenedSettings()

  // filter out slug column
  const filteredColumns = table.columns.filter((c) => c.type !== ColumnTypes.Slug)

  // Generate Field For Each Column
  const columns =
    filteredColumns.length === 0 ? [{ id: '', name: 'Text', type: ColumnTypes.Text } as ColumnAdapter] : filteredColumns

  for (const column of columns) {
    const formField = getFormFieldBasedOnColumnType(column.type)
    const fieldComp = ALL_COMPONENTS.find((c) => c.compId === formField)
    if (!fieldComp) continue

    // Generate Field
    const childBody = {
      id: uuid4(),
      groupId: getGroupId(fieldComp.compId),
      compId: fieldComp.compId,
      customTitle: column.name,
      icon: fieldComp.icon,
      isForm: true,
      isDetailPageComponent: false,
      children: [],
      parentId: formId,
      parentTitle: 'Form',
      parentType: 'form',
      title: column.name,
      inactive: false,
      nested: true,
    } as BuilderDroppableElement

    // Generate Field Settings
    const childSettings = parseFormFieldSettings(fieldComp, table, column, isUserTable)

    // Append Field
    elChildren.push(childBody as any)
    elSettings[childBody.id] = childSettings
  }

  // Add submit button
  const btnCompGroup = DEFAULT_COMPONENTS_GROUPS.find((g) => g.id === 'button')
  const btnComp = btnCompGroup?.components.find((c) => c.compId === 'button')
  if (btnCompGroup && btnComp) {
    // Generate Submit Button
    const btnBody = {
      id: uuid4(),
      groupId: btnCompGroup.id,
      compId: btnComp.compId,
      title: btnComp.title,
      icon: btnComp.icon,
      inactive: false,
      nested: btnComp.nested,
      isForm: btnComp.isForm,
      isDetailPageComponent: btnComp.isDetailPageComponent,
      children: [],
      parentId: formId,
      parentTitle: 'Form',
      parentType: 'form',
      removable: false,
      draggable: false,
      isSubmit: true,
    }

    // Generate Submit Button Settings
    const btnSettings = parseFormSubmitButtonSettings(btnComp, tables, flattenedSettings, table, formType)

    if (formType === 'form-add') {
      btnSettings[ComponentSettingKey.ButtonActionClick] = [
        ...btnSettings[ComponentSettingKey.ButtonActionClick],
        {
          id: uuid4(),
          type: IActionType.CloseOverlay,
          settings: {},
        },
      ]
    }

    // Append Submit Button
    elChildren.push(btnBody)
    elSettings[btnBody.id] = btnSettings
  }

  return {
    children: elChildren,
    childElSettings: elSettings,
  }
}
